import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { SharedService } from '@services/shared.service';
import { FormsModule } from '@angular/forms';
import { PagesService } from '@services/pages.service';
import { SecureStorageService } from '@services/secure-storage.service';

@Component({
  selector: 'app-developer',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './developer.component.html',
  styles: [
    `.promax {
        font-family: Segoe UI, Arial, sans-serif;
        color: #111;
        margin: 0;
        padding: 24px;
        display: flex;
        justify-content: center;
      }
      .wrap {
        width: 100%;
      }
      h1 {
        font-size: 22px;
        margin: 0 0 10px;
        color: #0b5cff;
      }
      h2 {
        font-size: 18px;
        margin: 0 0 8px;
        color: #0b5cff;
      }
      p {
        margin: 6px 0 14px;
        color: #444;
      }
      .card {
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 16px;
        margin-top: 16px;
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.04);
      }
      code {
        background: #f3f4f6;
        padding: 3px 6px;
        border-radius: 6px;
        font-family: monospace;
        font-size: 13px;
      }
      pre {
        background: #f3f4f6;
        padding: 10px;
        border-radius: 8px;
        overflow: auto;
        font-size: 13px;
      }
      ul {
        margin: 8px 0 0 18px;
        color: #444;
        line-height: 1.6;
        font-size: 14px;
      }
      // input {
      //   height: 56px;
      //   border-radius: 12px;
      //   background: #f5f5f9;
      //   padding: 21px 18px;
      //   font-size: 14px;
      //   font-weight: 400;
      //   line-height: 16px;
      //   text-align: left;
      //   border: none;
      //   color: #2e3b55;

      //   &:focus,
      //   &:active {
      //     background: #f5f5f9;
      //   }
      // }
      // label {
      //   text-align: left;
      //   color: #2e3b55;
      //   font-size: 18px;
      //   font-weight: 600;
      //   line-height: 21.48px;
      //   margin-bottom: 8px;
      // }
      h1 {
        font-family: 'Salsa' !important;
        font-size: 32px;
        font-weight: 400;
        line-height: 39.23px;
        text-align: left;
        color: #2e3b55;
      }
      h6 {
        padding-top: 18px;
        font-size: 22.46px;
        font-weight: 500;
        line-height: 33.69px;
        text-align: left;
        color: #828999;
      }

      .developer-card {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px;
      }

      .client-libraries-card .bottom {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        align-items: stretch;
      }

      .client-libraries-card .bottom .btn {
        width: 100%;
        min-height: 44px;
        white-space: normal;
        line-height: 1.25;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
      }

      @media (max-width: 992px) {
        .developer-card {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 576px) {
        .client-libraries-card .bottom {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class DeveloperComponent {
  isApiKey: boolean = false;
  apiKey: string = '';
  copied: boolean = false;
  clientName!: string;
  isDisabled = true; 
  sharedService = inject(SharedService);
  translateService = inject(TranslateService);
  pagesService = inject(PagesService);
  secureStorage = inject(SecureStorageService);
  constructor(private clipboard: Clipboard) { }
  ngOnInit(): void {
    this.getApiKey();
    this.setClientName();
  }
  setClientName(): void {
    const user = this.secureStorage.getItem<any>('userDetails');
    if (user) {
      this.clientName = user?.name || ''; // e.g. "Adnan Malik"
    }
  }
  generateApiKey(name: string): void {
    this.sharedService.showLoader();
    this.pagesService
      .update('GET_DEVELOPER_API_KEY', { client_name: name })
      .subscribe({
        next: (res: any) => {
          this.apiKey = res.api_key;
          this.copied = false;
          this.sharedService.hideLoader();
          this.sharedService.createNotification(
            'success',
            this.translateService.instant('developer.apiKeyGenerated')
          );
        },
        error: () => {
          this.sharedService.createNotification(
            'error',
            this.translateService.instant('developer.apiKeyGenerationFailed')
          );
          this.sharedService.hideLoader();
        },
      });
  }
  copyApiKey(apiKeyInput: HTMLInputElement): void {
    if (!this.apiKey) {
      this.sharedService.createNotification(
        'warning',
        this.translateService.instant('developer.noApiKeyToCopy')
      );
      return;
    }
    this.clipboard.copy(this.apiKey);
    apiKeyInput.select();
    this.copied = true;
    this.sharedService.createNotification(
      'success',
      this.translateService.instant('developer.apiKeyCopied')
    );
    setTimeout(() => {
      this.copied = false;
    }, 2000);
  }
  // ✅ Fixed PDF Download
  async downloadDummyPdf(): Promise<void> {
    this.sharedService.showLoader();
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable: any = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      doc.setProperties({ title: 'DigiContests API Documentation' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('DigiContests API Documentation', 40, 50);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      const intro =
        'This document describes how to access the contests endpoint. ' +
        'Use the API key generated in the Developer page.';
      doc.text(doc.splitTextToSize(intro, 520), 40, 80);
      doc.setFont('helvetica', 'bold');
      doc.text('Endpoint:', 40, 120);
      doc.setFont('helvetica', 'normal');
      doc.text(
        'GET https://xi5f-5y62-kbyr.n7e.xano.io/api:BB6HooOR/luqtaWebhook',
        110,
        120
      );
      // ---- Query Params (Table) ----
      const startY = 150;
      autoTable(doc, {
        startY,
        head: [['Param', 'Type', 'Required', 'Description']],
        body: [['apiKey', 'string', 'Yes', 'Your API key (client-specific)']],
        styles: { font: 'helvetica', fontSize: 11 },
        headStyles: { fontStyle: 'bold' },
        margin: { left: 40, right: 40 },
        tableWidth: 520,
      });
      let y = (doc as any).lastAutoTable?.finalY ?? startY + 20;
      y += 30;
      doc.setFont('helvetica', 'bold');
      doc.text('Example (cURL):', 40, y);
      y += 16;
      doc.setFont('courier', 'normal');
      const curl = `curl -X GET "https://xi5f-5y62-kbyr.n7e.xano.io/api:BB6HooOR/luqtaWebhook" \
  -H "Authorization: Bearer YOUR_API_KEY"`;
      doc.text(doc.splitTextToSize(curl, 520), 40, y);
      y += 40;
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 40, y);
      y += 16;
      doc.setFont('helvetica', 'normal');
      const notes = [
        '• Replace YOUR_API_KEY with the key generated on this page.',
        '• Keep your API key secret. Do not expose it in client-side code publicly.',
      ];
      doc.text(notes, 40, y);
      // ---- Open in new tab (show), fallback to download if blocked ----
      const blobUrl = doc.output('bloburl');
      const win = window.open(blobUrl, '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        doc.save('DigiContests_API_Documentation.pdf');
      }
      this.sharedService.createNotification(
        'success',
        this.translateService.instant('developer.pdfDownloadStarted')
      );
    } catch (e) {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('developer.pdfGenerationFailed') ||
        'PDF generation failed'
      );
    } finally {
      this.sharedService.hideLoader();
    }
  }
  // ✅ Postman Collection Download
  downloadCollection(): void {
    this.sharedService.showLoader();
    const collectionData = {
      info: {
        name: 'DigiContests API Collection',
        schema:
          'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      },
      item: [
        {
          name: 'Get Contests',
          request: {
            method: 'GET',
            header: [
              {
                key: 'Authorization',
                value: 'Bearer {{apiKey}}',
                type: 'text',
              },
            ],
            url: {
              raw: 'https://xi5f-5y62-kbyr.n7e.xano.io/api:BB6HooOR/luqtaWebhook',
              protocol: 'https',
              host: ['xi5f-5y62-kbyr', 'n7e', 'xano', 'io'],
              path: ['api:BB6HooOR', 'luqtaWebhook'],
            },
          },
          response: [],
        },
      ],
      variable: [{ key: 'apiKey', value: 'your api key' }],
    };
    const blob = new Blob([JSON.stringify(collectionData, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'DigiContests_API_Collection.json';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    this.sharedService.hideLoader();
    this.sharedService.createNotification(
      'success',
      this.translateService.instant('developer.collectionDownloadStarted')
    );
  }
  getApiKey(): void {
    this.sharedService.showLoader();
    this.pagesService.getAll('GET_DEVELOPER_API_KEY').subscribe({
      next: (res: any) => {
        if (res?.code === 'ERROR_FATAL' || res?.code?.startsWith?.('ERROR')) {
          this.sharedService.createNotification('info', res.message || 'No API key found. Please generate one.');
        } else if (res?.length > 0 && res[0]) {
          this.isApiKey = !!res[0].api_key;
          this.apiKey = res[0].api_key;
          this.clientName = res[0].client_name || '';
        }
        this.sharedService.hideLoader();
      },
      error: (err: any) => {
        const message = err?.error?.message || err?.message || 'Failed to fetch API key.';
        this.sharedService.createNotification('error', message);
        this.sharedService.hideLoader();
      },
    });
  }
}
